import { ProgressBar, Text, BlockStack, InlineStack } from '@shopify/polaris';
import type { ScanProgress, ScanProgressExtended } from '../types';
import { SCAN_STATUS_LABELS } from '../theme';

/**
 * T027: ScanProgressBar component (extended).
 * Shows a Polaris ProgressBar with product count, percentage, and ETA range (FR-017, FR-018).
 * Progress is capped at 95% until backend confirms completion (FR-033).
 * Uses theme config for status labels.
 *
 * @see documents/ui-enhancement-spec.md §15.3
 */
interface ScanProgressBarProps {
  scanProgress: ScanProgress | ScanProgressExtended;
}

export function ScanProgressBar({ scanProgress }: Readonly<ScanProgressBarProps>) {
  const { status, scannedProducts, totalProducts } = scanProgress;

  // Use 'progress' field from ScanProgressExtended if available, otherwise compute
  let rawPercentage: number;
  if ('progress' in scanProgress && typeof scanProgress.progress === 'number') {
    rawPercentage = scanProgress.progress;
  } else if ('percentage' in scanProgress && typeof scanProgress.percentage === 'number') {
    rawPercentage = scanProgress.percentage;
  } else {
    rawPercentage = totalProducts > 0 ? Math.round((scannedProducts / totalProducts) * 100) : 0;
  }

  // Cap at 95% for non-complete states (FR-033)
  // Force 100% when COMPLETED regardless of raw percentage
  const isTerminal = status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED';
  let percentage: number;
  if (status === 'COMPLETED') {
    percentage = 100;
  } else if (isTerminal) {
    percentage = rawPercentage;
  } else {
    percentage = Math.min(rawPercentage, 95);
  }

  // When completed, display scannedProducts as both values to avoid "15 / 25" mismatch
  const displayTotal = status === 'COMPLETED' ? scannedProducts : totalProducts;

  const isActive = status === 'RUNNING' || status === 'QUEUED';

  // Extract ETA if available (ScanProgressExtended)
  const etaLower = 'etaLowerSeconds' in scanProgress ? scanProgress.etaLowerSeconds : null;
  const etaUpper = 'etaUpperSeconds' in scanProgress ? scanProgress.etaUpperSeconds : null;

  let progressTone: 'highlight' | 'success' | 'critical' = 'critical';
  if (isActive) progressTone = 'highlight';
  else if (status === 'COMPLETED') progressTone = 'success';

  return (
    <BlockStack gap="200">
      <InlineStack align="space-between">
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {getStatusLabel(status)}
        </Text>
        <Text as="span" variant="bodyMd" tone="subdued">
          {scannedProducts} / {displayTotal} products
        </Text>
      </InlineStack>

      <ProgressBar
        progress={percentage}
        size="small"
        tone={progressTone}
      />

      <InlineStack align="space-between">
        <Text as="span" variant="bodySm" tone="subdued">
          {percentage}% complete
        </Text>
        {isActive && etaLower != null && etaUpper != null && (
          <Text as="span" variant="bodySm" tone="subdued">
            ~{formatEta(etaLower)}–{formatEta(etaUpper)} remaining
          </Text>
        )}
      </InlineStack>
    </BlockStack>
  );
}

function getStatusLabel(status: string): string {
  return SCAN_STATUS_LABELS[status] ?? 'Unknown';
}

function formatEta(seconds: number): string {
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} min`;
}
